import { useMemo } from 'react';
import { generatePath } from 'react-router';

import { useTranslation } from 'react-i18next';
import { COUNTRY_TO_LANG } from 'src/modules/locales/config';

export const LANG_PARAM = '/:lang?/';

export const useRoute = () => {
  const {
    i18n: { language }
  } = useTranslation();

  const lang = language === COUNTRY_TO_LANG.EN ? '' : language;

  const ROUTE = useMemo(() => {
    return {
      AUTH: { path: 'auth', get: () => generatePath(LANG_PARAM + ROUTE.AUTH.path, { lang }) },
      AVIATOR: {
        path: 'game/34642',
        get: () => generatePath(LANG_PARAM + ROUTE.AVIATOR.path, { lang })
      },
      MAIN: { path: '/', get: () => generatePath(LANG_PARAM + ROUTE.MAIN.path, { lang }) },
      TOURNAMENTS: {
        path: 'tournaments',
        get: () => generatePath(LANG_PARAM + ROUTE.TOURNAMENTS.path, { lang })
      },
      NETWORK_TOURNAMENT: {
        path: 'tournaments/:tournamentCode',
        get: (tournamentCode: string) =>
          generatePath(LANG_PARAM + ROUTE.NETWORK_TOURNAMENT.path, { lang, tournamentCode })
      },
      TOURNAMENTS_LOCAL: {
        path: 'tournaments/local/:tournamentId',
        get: (tournamentId: string) =>
          generatePath(LANG_PARAM + ROUTE.TOURNAMENTS_LOCAL.path, { lang, tournamentId })
      },
      BONUSES: {
        path: 'bonuses',
        get: () => generatePath(LANG_PARAM + ROUTE.BONUSES.path, { lang })
      },
      BONUS: {
        path: 'bonuses/:bonus',
        get: (bonus: string) => generatePath(LANG_PARAM + ROUTE.BONUS.path, { lang, bonus })
      },
      INFO_PAGES: {
        path: 'info_pages/:page',
        get: (page: string) => generatePath(LANG_PARAM + ROUTE.INFO_PAGES.path, { lang, page })
      },
      FAQ: { path: 'faq', get: () => generatePath(LANG_PARAM + ROUTE.FAQ.path, { lang }) },
      SHOP: { path: 'shop', get: () => generatePath(LANG_PARAM + ROUTE.SHOP.path, { lang }) },
      ['REDIRECT-LOBBY']: {
        path: 'redirect-lobby',
        get: () => generatePath(LANG_PARAM + ROUTE.REDIRECT_LOBBY.path, { lang })
      },
      GAME: {
        path: 'game/:gameId?/:demo?',
        get: (gameId: string, demo: string) =>
          generatePath(LANG_PARAM + ROUTE.GAME.path, { lang, gameId, demo })
      },
      GAMES: {
        path: 'games/:categoryId?',
        get: (categoryId?: string) =>
          generatePath(LANG_PARAM + ROUTE.GAMES.path, { lang, categoryId })
      },
      MAIN_GAMES: {
        path: 'games',
        get: () => generatePath(LANG_PARAM + ROUTE.MAIN_GAMES.path, { lang })
      },
      MAIN_PROVIDERS: {
        path: 'provider',
        get: () => generatePath(LANG_PARAM + ROUTE.MAIN_PROVIDERS.path, { lang })
      },
      GAMES_LIVE: {
        path: 'games/live',
        get: () => generatePath(LANG_PARAM + ROUTE.GAMES_LIVE.path, { lang })
      },
      GAMES_JACKPOTS: {
        path: 'games/jackpots',
        get: () => generatePath(LANG_PARAM + ROUTE.GAMES_JACKPOTS.path, { lang })
      },
      GAMES_SLOTS: {
        path: 'games/slots',
        get: () => generatePath(LANG_PARAM + ROUTE.GAMES_SLOTS.path, { lang })
      },
      VIP: { path: 'vip', get: () => generatePath(LANG_PARAM + ROUTE.VIP.path, { lang }) },
      PWA_INSTRUCTION: {
        path: 'pwa-instruction/:deviceId?',
        get: (deviceId: string) =>
          generatePath(LANG_PARAM + ROUTE.PWA_INSTRUCTION.path, { lang, deviceId })
      },
      PWA: { path: 'pwa', get: () => generatePath(LANG_PARAM + ROUTE.PWA.path, { lang }) },
      JACKPOTS: {
        path: 'jackpots',
        get: () => generatePath(LANG_PARAM + ROUTE.JACKPOTS.path, { lang })
      },
      REGISTRATION: {
        path: 'registration',
        get: () => generatePath(LANG_PARAM + ROUTE.REGISTRATION.path, { lang })
      },
      ACCOUNT: {
        path: 'account',
        get: () => generatePath(LANG_PARAM + ROUTE.ACCOUNT.path, { lang })
      },
      ACCOUNT_PROFILE_MAIN: {
        path: 'data',
        get: () =>
          generatePath(
            LANG_PARAM + ROUTE.ACCOUNT.path + '/profile/' + ROUTE.ACCOUNT_PROFILE_MAIN.path,
            { lang }
          )
      },
      ACCOUNT_PROFILE: {
        path: 'profile/*',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_PROFILE.path, { lang })
      },
      ACCOUNT_WITHDRAWAL: {
        path: 'withdrawal/*',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_WITHDRAWAL.path, {
            lang
          })
      },
      ACCOUNT_BONUS: {
        path: 'bonus/*',
        get: (page = '') =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_BONUS.path, { lang }) +
          '/' +
          page
      },
      ACCOUNT_DEPOSIT: {
        path: 'deposit/*',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_DEPOSIT.path, { lang })
      },
      ACCOUNT_WALLET_HISTORY: {
        path: 'withdrawal/history',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_WALLET_HISTORY.path, {
            lang
          })
      },
      ACCOUNT_PROFILE_TOURNAMENTS: {
        path: 'tournaments/*',
        get: () =>
          generatePath(
            LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_PROFILE_TOURNAMENTS.path,
            { lang }
          )
      },
      ACCOUNT_USER_BALANCE: {
        path: 'userBalance/*',
        get: (page = '') =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_USER_BALANCE.path, {
            lang
          }) +
          '/' +
          page
      },
      ACCOUNT_JACKPOTS: {
        path: 'jackpots/*',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_JACKPOTS.path, {
            lang
          })
      },
      ACCOUNT_VIP: {
        path: 'vip',
        get: () =>
          generatePath(LANG_PARAM + ROUTE.ACCOUNT.path + '/' + ROUTE.ACCOUNT_VIP.path, { lang })
      },
      DEPOSIT_LINK: {
        path: 'deposit',
        get: () => generatePath(LANG_PARAM + ROUTE.DEPOSIT_LINK.path, { lang })
      },
      DEPOSIT_SUCCESS: {
        path: 'deposit-success/:merchantId?',
        get: (merchantId: string) =>
          generatePath(LANG_PARAM + ROUTE.DEPOSIT_SUCCESS.path, { lang, merchantId })
      },
      DEPOSIT_FAILURE: {
        path: 'deposit-failure/*',
        get: () => generatePath(LANG_PARAM + ROUTE.DEPOSIT_FAILURE.path, { lang })
      },
      DEPOSIT_CANCEL: {
        path: 'deposit-cancel/*',
        get: () => generatePath(LANG_PARAM + ROUTE.DEPOSIT_CANCEL.path, { lang })
      },
      DEPOSIT_RETURN: {
        path: 'deposit-return/:merchantId?',
        get: (merchantId: string) =>
          generatePath(LANG_PARAM + ROUTE.DEPOSIT_RETURN.path, { lang, merchantId })
      },
      ACTIVATE_BONUS: {
        path: 'activate-bonus/:bonusId',
        get: (bonusId: string) =>
          generatePath(LANG_PARAM + ROUTE.ACTIVATE_BONUS.path, { lang, bonusId })
      },
      LANDING_WELCOME_BONUS: {
        path: 'landing/welcome-bonus',
        get: () => generatePath(LANG_PARAM + ROUTE.LANDING_WELCOME_BONUS.path, { lang })
      },
      REGISTER_REFERRAL: {
        path: 'register-referral/:referral',
        get: (referral: string) =>
          generatePath(LANG_PARAM + ROUTE.REGISTER_REFERRAL.path, { lang, referral })
      },
      REDIRECT_LOBBY: {
        path: 'redirect-lobby',
        get: () => generatePath(LANG_PARAM + ROUTE.REDIRECT_LOBBY.path, { lang })
      },
      INFO_PAGES_CODE: {
        path: 'info_pages/:code',
        get: (code: string) => generatePath(LANG_PARAM + ROUTE.INFO_PAGES_CODE.path, { lang, code })
      },
      INFO_PAGES_POLICY: {
        path: 'info_pages/privacy_policy',
        get: () => generatePath(LANG_PARAM + ROUTE.INFO_PAGES_POLICY.path, { lang })
      },
      INFO_PAGES_TERMS: {
        path: 'info_pages/terms_and_conditions',
        get: () => generatePath(LANG_PARAM + ROUTE.INFO_PAGES_TERMS.path, { lang })
      },
      LUCKY_PIGEON_OPENER: {
        path: 'lucky-pigeon',
        get: () => generatePath(LANG_PARAM + ROUTE.LUCKY_PIGEON_OPENER.path, { lang })
      },
      ACTION: { path: 'action', get: () => generatePath(LANG_PARAM + ROUTE.ACTION.path, { lang }) },
      BLOCKED_COUNTRY: {
        path: 'blocked-country',
        get: () => generatePath(LANG_PARAM + ROUTE.BLOCKED_COUNTRY.path, { lang })
      },
      404: { path: '404', get: () => generatePath(LANG_PARAM + ROUTE[404].path, { lang }) },
      ALL_ROUTES: {
        path: '*',
        get: () => generatePath(LANG_PARAM + ROUTE.ALL_ROUTES.path, { lang })
      },
      CUSTOM: {
        get: (path: string) => {
          return lang === 'en' ? path : generatePath(LANG_PARAM + path, { lang });
        }
      },
      HALLOWEEN: {
        path: '/halloween',
        get: () => generatePath(LANG_PARAM + ROUTE.HALLOWEEN.path, { lang })
      },
      CATCH_GAME: {
        path: 'chick-hunt',
        get: () => generatePath(LANG_PARAM + ROUTE.CATCH_GAME.path, { lang })
      },
      ACOUNT_BONUS_PROMO: {
        path: '/account/bonus/bonus-account',
        get: () => generatePath(LANG_PARAM + ROUTE.ACOUNT_BONUS_PROMO.path, { lang })
      }
    };
  }, [lang]);

  const getPath = (path: string) => generatePath(LANG_PARAM + path, { lang });

  return { ROUTE, getPath };
};

export type RoutesType = keyof ReturnType<typeof useRoute>['ROUTE'];
